/**
 * A job's Interviewer tab: the saved interviewer (or Rio's default, drafted
 * from the posting), the same card and brief as when posting, and Save.
 *
 * Saving sends the version the recruiter started from. If someone else saved
 * in between, nothing is overwritten: the tab says so and offers their
 * version instead. "Reset to Rio's default" removes the saved interviewer, so
 * Rio drafts from the posting again.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button, Popconfirm, message } from 'antd';
import type {
  HistoryVersion,
  InterviewerConfig,
  Posting,
} from '../../services/interviewer.types';
import { InterviewerError } from '../../services/interviewer';
import { useInterviewerService } from '../../interviewer/serviceContext';
import { useInterviewer } from '../../interviewer/useInterviewer';
import { sameConfig } from '../../interviewer/model';
import InterviewerSetup from './index';
import styles from './tab.module.css';

interface Props {
  jobId: number;
  posting: Posting;
}

type LoadState = 'loading' | 'loaded' | 'failed';

const formatWhen = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      }).format(date);
};

const InterviewerTab = ({ jobId, posting }: Props) => {
  const ids = useId();
  const service = useInterviewerService();
  const state = useInterviewer({ posting });
  const [messageApi, contextHolder] = message.useMessage();
  const [load, setLoad] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState('');
  const [saved, setSaved] = useState<InterviewerConfig | null>(null);
  // The version to save against: kept apart from the config, because a reset
  // leaves no config but still moves the version on.
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [conflict, setConflict] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<HistoryVersion[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const postingRef = useRef(posting);
  postingRef.current = posting;

  const loadHistory = useCallback(async () => {
    setHistory(null);
    setHistoryError(null);
    try {
      setHistory(await service.history(jobId));
    } catch (error) {
      setHistoryError(
        error instanceof InterviewerError
          ? error.message
          : "History couldn't be loaded.",
      );
    }
  }, [jobId, service]);

  const fetchSaved = useCallback(async () => {
    setLoad('loading');
    setConflict(false);
    setSaveError(null);
    try {
      const result = await service.get(jobId);
      setSaved(result.config);
      setSavedVersion(result.version);
      setProblems([]);
      if (result.config) {
        state.load(result.config, result.preview);
      } else {
        await state.draft(postingRef.current);
      }
      setLoad('loaded');
    } catch (error) {
      setLoadError(
        error instanceof InterviewerError
          ? error.message
          : "This job's interviewer couldn't be loaded.",
      );
      setLoad('failed');
    }
    // `state.load` and `state.draft` are stable callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, service]);

  useEffect(() => {
    fetchSaved();
  }, [fetchSaved]);

  const dirty = saved
    ? !sameConfig(state.config, saved)
    : state.touched && state.config !== null;

  const save = async () => {
    if (!state.config) return;
    setSaving(true);
    setSaveError(null);
    setProblems([]);
    try {
      const result = await service.save(jobId, state.config, savedVersion);
      setSaved(result.config);
      setSavedVersion(result.version);
      state.load(result.config, result.preview);
      if (historyOpen) loadHistory();
      else setHistory(null);
      messageApi.open({ type: 'success', content: 'Interviewer saved' });
    } catch (error) {
      if (error instanceof InterviewerError && error.kind === 'conflict') {
        setConflict(true);
      } else if (
        error instanceof InterviewerError &&
        error.kind === 'validation' &&
        error.errors.length > 0
      ) {
        setProblems(error.errors.map((e) => e.message));
      } else {
        setSaveError(
          error instanceof InterviewerError
            ? error.message
            : 'Unable to save. Check your connection and try again.',
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    try {
      const result = await service.reset(jobId);
      setSaved(null);
      setSavedVersion(result.version);
      if (historyOpen) loadHistory();
      else setHistory(null);
      await state.draft(postingRef.current);
      messageApi.open({ type: 'success', content: "Back to Rio's default" });
    } catch (error) {
      setSaveError(
        error instanceof InterviewerError
          ? error.message
          : 'Unable to reset. Check your connection and try again.',
      );
    } finally {
      setResetting(false);
    }
  };

  const toggleHistory = () => {
    const opening = !historyOpen;
    setHistoryOpen(opening);
    if (opening && !history) loadHistory();
  };

  const status = dirty
    ? 'Unsaved changes'
    : saved
      ? `Saved${savedVersion ? `, version ${savedVersion}` : ''}`
      : "Rio's default";

  if (load === 'failed') {
    return (
      <div className={styles.bar} role="alert">
        <div>
          <p className={styles.barTitle}>
            This job&rsquo;s interviewer couldn&rsquo;t be loaded.
          </p>
          <p className={styles.barHint}>{loadError}</p>
        </div>
        <Button shape="round" onClick={fetchSaved}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.tab}>
      {contextHolder}
      <div className={styles.bar}>
        <div>
          <p className={styles.barTitle}>
            {saved
              ? 'Rio interviews candidates for this job as set up here.'
              : 'Rio interviews candidates with its default for this job.'}
          </p>
          <p className={styles.barHint}>
            {saved
              ? 'Changes apply to interviews that start after you save.'
              : 'Change anything below and save to make it this job’s interviewer.'}
          </p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.link}
            aria-expanded={historyOpen}
            aria-controls={`${ids}-history`}
            onClick={toggleHistory}
          >
            History
          </button>
          {saved && (
            <Popconfirm
              title="Reset to Rio's default?"
              description="Your saved interviewer for this job is removed, and Rio drafts from the posting again."
              okText="Reset interviewer"
              cancelText="Cancel"
              onConfirm={reset}
            >
              <Button type="text" shape="round" loading={resetting}>
                Reset to Rio&rsquo;s default
              </Button>
            </Popconfirm>
          )}
          <Button
            type="primary"
            shape="round"
            disabled={!dirty || state.busy || load !== 'loaded'}
            loading={saving}
            onClick={save}
          >
            Save
          </Button>
        </div>
      </div>

      {conflict && (
        <div className={styles.alert} role="alert">
          <div>
            <p className={styles.barTitle}>
              Someone else saved changes to this interviewer while you were
              editing.
            </p>
            <p className={styles.barHint}>
              Load their version to continue. Your unsaved changes will be
              replaced.
            </p>
          </div>
          <Button shape="round" onClick={fetchSaved}>
            Load the latest version
          </Button>
        </div>
      )}
      {problems.length > 0 && (
        <div className={styles.alert} role="alert">
          <div>
            <p className={styles.barTitle}>Rio can&rsquo;t save this yet.</p>
            <ul className={styles.problems}>
              {problems.map((p) => (
                <li key={p} className={styles.barHint}>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {saveError && (
        <p className={styles.error} role="alert">
          {saveError}
        </p>
      )}

      {historyOpen && (
        <section
          id={`${ids}-history`}
          className={styles.history}
          aria-label="Version history"
        >
          {historyError && <p className={styles.barHint}>{historyError}</p>}
          {!history && !historyError && (
            <p className={styles.barHint}>Loading history…</p>
          )}
          {history && history.length === 0 && (
            <p className={styles.barHint}>
              No saved versions yet. Rio&rsquo;s default is in use.
            </p>
          )}
          {history && history.length > 0 && (
            <ol className={styles.versions}>
              {history.map((v) => (
                <li key={v.version} className={styles.version}>
                  <span className={styles.versionNumber}>v{v.version}</span>
                  <span className={styles.versionSummary}>{v.summary}</span>
                  <span className={styles.versionMeta}>
                    {v.updatedBy}
                    {formatWhen(v.updatedAt)
                      ? `, ${formatWhen(v.updatedAt)}`
                      : ''}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      <InterviewerSetup
        state={state}
        posting={posting}
        status={status}
        emptyMessage="This job has no description for Rio to draft from yet."
      />
    </div>
  );
};

export default InterviewerTab;
